import { useState, useCallback, useRef } from 'react';
import { openaiService } from '../services/openaiService';
import { 
  GenerationParams, 
  StreamingState, 
  GeneratedStory 
} from '../types/streaming.types';
import { 
  LessonPlan, 
  LessonSection, 
  Story 
} from '../types/lessonPlan.types';

interface UseStreamingGenerationReturn {
  generateIntroductionSection: (lessonPlan: LessonPlan) => Promise<void>;
  generateIndependentSection: (lessonPlan: LessonPlan) => Promise<void>;
  generateBothSections: (lessonPlan: LessonPlan) => Promise<void>;
  regenerateContent: (
    lessonPlan: LessonPlan,
    sectionType: 'introduction' | 'independent',
    contentType: 'story' | 'activities'
  ) => Promise<void>;
  cancelGeneration: () => void;
  streamingStates: {
    [key: string]: StreamingState;
  };
  streamingContent: {
    [key: string]: string;
  };
  isGenerating: boolean;
}

/**
 * Hook for managing streaming content generation
 * Orchestrates OpenAI API calls with real-time updates
 */
export const useStreamingGeneration = (
  onSectionUpdate: (
    sectionType: 'introduction' | 'independent',
    updates: Partial<LessonSection>
  ) => void,
  selectedStandard?: { code: string; description: string; _id: string } | null
): UseStreamingGenerationReturn => {
  
  const [streamingStates, setStreamingStates] = useState<{
    [key: string]: StreamingState;
  }>({});
  
  const [streamingContent, setStreamingContent] = useState<{
    [key: string]: string;
  }>({});
  
  const [isGenerating, setIsGenerating] = useState(false);
  const abortController = useRef<AbortController | null>(null);
  // Guard to ignore any late updates from streams after a cancel
  const ignoreUpdatesAfterCancel = useRef<boolean>(false);

  // Update streaming state for a specific content type
  const updateStreamingState = useCallback((
    key: string,
    updates: Partial<StreamingState>
  ) => {
    setStreamingStates(prev => ({
      ...prev,
      [key]: { ...prev[key], ...updates }
    }));
  }, []);

  // Update streaming content for a specific content type
  const updateStreamingContent = useCallback((
    key: string,
    content: string
  ) => {
    setStreamingContent(prev => ({
      ...prev,
      [key]: content
    }));
  }, []);

  // Cancel all ongoing generation
  const cancelGeneration = useCallback(() => {
    if (abortController.current) {
      abortController.current.abort();
      abortController.current = null;
    }
    // Ensure we ignore any in-flight onProgress/onComplete callbacks
    ignoreUpdatesAfterCancel.current = true;
    
    setIsGenerating(false);
    
    // Reset all streaming states
    Object.keys(streamingStates).forEach(key => {
      updateStreamingState(key, {
        isActive: false,
        progress: 0,
        currentContent: '',
        error: undefined
      });
    });
  }, [streamingStates, updateStreamingState]);

  // Generate story content
  const generateStory = useCallback(async (
    params: GenerationParams,
    streamingKey: string,
    signal?: AbortSignal
  ): Promise<Story> => {
    updateStreamingState(streamingKey, {
      isActive: true,
      progress: 0,
      wordsPerMinute: 0,
      estimatedCompletion: 30,
      currentContent: '',
      error: undefined
    });
    updateStreamingContent(streamingKey, '');

    const startTime = Date.now();

    try {
      const generatedStory = await openaiService.generateStory(params, {
        onStart: () => {
          if (ignoreUpdatesAfterCancel.current) return;
          updateStreamingState(streamingKey, {
            isActive: true,
            progress: 0,
            wordsPerMinute: 0,
            estimatedCompletion: 0
          });
          updateStreamingContent(streamingKey, '');
        },
        onProgress: (content: string, progress: number) => {
          if (ignoreUpdatesAfterCancel.current) return;
          updateStreamingContent(streamingKey, content);
          
          // Calculate metrics for display
          const words = content.split(' ').length;
          const timeElapsed = (Date.now() - startTime) / 1000 / 60; // minutes
          const wordsPerMinute = timeElapsed > 0 ? Math.round(words / timeElapsed) : 0;
          
          updateStreamingState(streamingKey, {
            isActive: true,
            progress,
            wordsPerMinute,
            estimatedCompletion: 0
          });
        },
        onComplete: (finalContent: string) => {
          if (ignoreUpdatesAfterCancel.current) return;
          updateStreamingState(streamingKey, {
            isActive: false,
            progress: 100,
            wordsPerMinute: 0,
            estimatedCompletion: 0
          });
        },
        onError: (error: Error) => {
          if (ignoreUpdatesAfterCancel.current) return;
          updateStreamingState(streamingKey, {
            isActive: false,
            progress: 0,
            error: error.message
          });
          console.error('Error generating story:', error);
        }
      });

      return generatedStory;

    } catch (error) {
      updateStreamingState(streamingKey, {
        isActive: false,
        progress: 0,
        error: error instanceof Error ? error.message : 'Generation failed'
      });
      throw error;
    }
  }, [updateStreamingState, updateStreamingContent]);

  // Generate activities content
  const generateActivities = useCallback(async (
    params: GenerationParams,
    storyTitle: string,
    streamingKey: string,
    activitiesType: 'introduction' | 'practice',
    signal?: AbortSignal
  ): Promise<string> => {
    updateStreamingState(streamingKey, {
      isActive: true,
      progress: 0,
      wordsPerMinute: 0,
      estimatedCompletion: 20,
      currentContent: '',
      error: undefined
    });
    updateStreamingContent(streamingKey, '');

    const startTime = Date.now();

    try {
      // Add story title to params
      const paramsWithStoryTitle = { ...params, storyTitle };

      const activitiesMethod = activitiesType === 'introduction' 
        ? openaiService.generateIntroductionActivities.bind(openaiService)
        : openaiService.generatePracticeActivities.bind(openaiService);

      const activities = await activitiesMethod(paramsWithStoryTitle, {
        onStart: () => {
          if (ignoreUpdatesAfterCancel.current) return;
          updateStreamingState(streamingKey, {
            isActive: true,
            progress: 0,
            wordsPerMinute: 0,
            estimatedCompletion: 0
          });
          updateStreamingContent(streamingKey, '');
        },
        onProgress: (content: string, progress: number) => {
          if (ignoreUpdatesAfterCancel.current) return;
          updateStreamingContent(streamingKey, content);
          
          // Calculate metrics
          const words = content.split(' ').length;
          const timeElapsed = (Date.now() - startTime) / 1000 / 60; // minutes
          const wordsPerMinute = timeElapsed > 0 ? Math.round(words / timeElapsed) : 0;
          
          updateStreamingState(streamingKey, {
            isActive: true,
            progress,
            wordsPerMinute,
            estimatedCompletion: 0
          });
        },
        onComplete: (finalContent: string) => {
          if (ignoreUpdatesAfterCancel.current) return;
          updateStreamingState(streamingKey, {
            isActive: false,
            progress: 100,
            wordsPerMinute: 0,
            estimatedCompletion: 0
          });
        },
        onError: (error: Error) => {
          if (ignoreUpdatesAfterCancel.current) return;
          updateStreamingState(streamingKey, {
            isActive: false,
            progress: 0,
            error: error.message
          });
        }
      });

      return activities;

    } catch (error) {
      updateStreamingState(streamingKey, {
        isActive: false,
        progress: 0,
        error: error instanceof Error ? error.message : 'Generation failed'
      });
      throw error;
    }
  }, [updateStreamingState, updateStreamingContent]);

  // Generate introduction section (story + activities)
  const generateIntroductionSection = useCallback(async (lessonPlan: LessonPlan) => {
    setIsGenerating(true);
    abortController.current = new AbortController();
    ignoreUpdatesAfterCancel.current = false;

    // Clear existing content and show streaming state
    onSectionUpdate('introduction', { 
      story: null,
      activities: '',
      isGenerated: false,
      isStreaming: true 
    });

    try {
      const baseParams: GenerationParams = {
        subject: lessonPlan.form.subject,
        standardId: lessonPlan.form.standardId,
        standard: selectedStandard ? {
          code: selectedStandard.code,
          description: selectedStandard.description
        } : undefined,
        // Legacy ELA fields
        lexileLevel: lessonPlan.form.lexileLevel,
        genre: lessonPlan.form.genre,
        paragraphCount: lessonPlan.form.paragraphCount,
        // Generic fields
        difficulty: lessonPlan.form.difficulty,
        contentType: lessonPlan.form.contentType,
        contentLength: lessonPlan.form.contentLength,
        // Common fields
        topic: lessonPlan.form.topic,
        independentPracticeTopic: lessonPlan.form.independentPracticeTopic,
        section: 'introduction',
        generationType: 'story',
        language: lessonPlan.form.language,
        revisingAndEditing: lessonPlan.form.revisingAndEditing
      };

      // Generate in the selected base language (English or Spanish). Translation is separate.
      const primaryLanguage = (lessonPlan.form.language || 'english').toLowerCase();

      // Generate story first (primary language)
      const story = await generateStory({ ...baseParams, language: primaryLanguage }, 'intro-story', abortController.current?.signal);
      
      // Check if generation was cancelled before updating state
      if (abortController.current?.signal.aborted) {
        console.log('Generation cancelled after story generation');
        throw new Error('Request cancelled by user');
      }
      
      onSectionUpdate('introduction', { 
        story,
        isGenerated: true,
        isStreaming: false 
      });

      // Then generate activities (primary language) - include story content for alignment
      const activitiesParams = { 
        ...baseParams, 
        language: primaryLanguage, 
        contentType: 'activities' as const,
        storyContent: story.content // Pass instructional content for Math alignment
      };
      const activities = await generateActivities(
        activitiesParams, 
        story.title, 
        'intro-activities',
        'introduction',
        abortController.current?.signal
      );

      onSectionUpdate('introduction', { 
        activities,
        isGenerated: true,
        isStreaming: false 
      });

      // Translation is now handled by manual button, not automatic

    } catch (error) {
      console.error('Error generating introduction section:', error);
      
      // Re-throw abort errors to stop further processing
      if (error instanceof Error && (error.name === 'AbortError' || error.message.includes('cancel'))) {
        throw error;
      }
    } finally {
      setIsGenerating(false);
      abortController.current = null;
    }
  }, [generateStory, generateActivities, onSectionUpdate]);

  // Generate independent practice section (different story + practice activities)
  const generateIndependentSection = useCallback(async (lessonPlan: LessonPlan) => {
    setIsGenerating(true);
    abortController.current = new AbortController();
    ignoreUpdatesAfterCancel.current = false;

    // Clear existing content and show streaming state
    onSectionUpdate('independent', { 
      story: null,
      activities: '',
      isGenerated: false,
      isStreaming: true 
    });

    try {
      const baseParams: GenerationParams = {
        subject: lessonPlan.form.subject,
        standardId: lessonPlan.form.standardId,
        standard: selectedStandard ? {
          code: selectedStandard.code,
          description: selectedStandard.description
        } : undefined,
        // Legacy ELA fields
        lexileLevel: lessonPlan.form.lexileLevel,
        genre: lessonPlan.form.genre,
        paragraphCount: lessonPlan.form.paragraphCount,
        // Generic fields
        difficulty: lessonPlan.form.difficulty,
        contentType: lessonPlan.form.contentType,
        contentLength: lessonPlan.form.contentLength,
        // Common fields
        topic: lessonPlan.form.topic,
        independentPracticeTopic: lessonPlan.form.independentPracticeTopic,
        section: 'independent',
        generationType: 'story',
        language: lessonPlan.form.language,
        revisingAndEditing: lessonPlan.form.revisingAndEditing
      };

      // Generate in the selected base language (English or Spanish). Translation is separate.
      const primaryLanguage = (lessonPlan.form.language || 'english').toLowerCase();

      // Generate different story (primary language) - USE SAME STANDARD
      const story = await generateStory({ ...baseParams, language: primaryLanguage }, 'practice-story', abortController.current?.signal);
      
      // Check if generation was cancelled before updating state
      if (abortController.current?.signal.aborted) {
        console.log('Generation cancelled after story generation');
        throw new Error('Request cancelled by user');
      }
      
      onSectionUpdate('independent', { 
        story,
        isGenerated: true,
        isStreaming: false 
      });

      // Generate practice activities (primary language) - include story content for alignment
      const activitiesParams = { 
        ...baseParams, 
        language: primaryLanguage, 
        contentType: 'activities' as const,
        storyContent: story.content // Pass instructional content for Math alignment
      };
      const activities = await generateActivities(
        activitiesParams, 
        story.title, 
        'practice-activities',
        'practice',
        abortController.current?.signal
      );

      onSectionUpdate('independent', { 
        activities,
        isGenerated: true,
        isStreaming: false 
      });

      // Translation is now handled by manual button, not automatic

    } catch (error) {
      console.error('Error generating independent section:', error);
      
      // Re-throw abort errors to stop further processing
      if (error instanceof Error && (error.name === 'AbortError' || error.message.includes('cancel'))) {
        throw error;
      }
    } finally {
      setIsGenerating(false);
      abortController.current = null;
    }
  }, [generateStory, generateActivities, onSectionUpdate]);

  // Generate individual story or activities
  const regenerateContent = useCallback(async (
    lessonPlan: LessonPlan,
    sectionType: 'introduction' | 'independent',
    contentType: 'story' | 'activities'
  ) => {
    setIsGenerating(true);
    abortController.current = new AbortController();
    ignoreUpdatesAfterCancel.current = false;

    try {
      const params: GenerationParams = {
        subject: lessonPlan.form.subject,
        standardId: lessonPlan.form.standardId,
        standard: selectedStandard ? {
          code: selectedStandard.code,
          description: selectedStandard.description
        } : undefined,
        // Legacy ELA fields
        lexileLevel: lessonPlan.form.lexileLevel,
        genre: lessonPlan.form.genre,
        paragraphCount: lessonPlan.form.paragraphCount,
        // Generic fields
        difficulty: lessonPlan.form.difficulty,
        contentType: lessonPlan.form.contentType,
        contentLength: lessonPlan.form.contentLength,
        // Common fields
        topic: lessonPlan.form.topic,
        independentPracticeTopic: lessonPlan.form.independentPracticeTopic,
        section: sectionType,
        generationType: contentType,
        language: lessonPlan.form.language,
        revisingAndEditing: lessonPlan.form.revisingAndEditing
      };

      if (contentType === 'story') {
        // Clear only the story and show streaming
        onSectionUpdate(sectionType, { 
          story: null,
          isStreaming: true 
        });

        const streamingKey = sectionType === 'introduction' ? 'intro-story' : 'practice-story';
        const story = await generateStory(params, streamingKey, abortController.current?.signal);
        
        onSectionUpdate(sectionType, { 
          story,
          isStreaming: false 
        });
      } else {
        // Clear only the activities and show streaming
        onSectionUpdate(sectionType, { 
          activities: '',
          isStreaming: true 
        });

        const streamingKey = sectionType === 'introduction' ? 'intro-activities' : 'practice-activities';
        const activitiesType = sectionType === 'introduction' ? 'introduction' : 'practice';
        
        // Get existing story title and content for context
        const currentSection = sectionType === 'introduction' 
          ? lessonPlan.sections.introduction 
          : lessonPlan.sections.independent;
        const storyTitle = currentSection.story?.title || 'Untitled Story';
        const storyContent = currentSection.story?.content || '';
        
        const activities = await generateActivities(
          { ...params, contentType: 'activities', storyContent },
          storyTitle,
          streamingKey,
          activitiesType,
          abortController.current?.signal
        );

        onSectionUpdate(sectionType, { 
          activities,
          isStreaming: false 
        });
      }
    } catch (error) {
      console.error(`Error regenerating ${contentType} for ${sectionType}:`, error);
      
      // Re-throw abort errors to stop further processing
      if (error instanceof Error && (error.name === 'AbortError' || error.message.includes('cancel'))) {
        throw error;
      }
    } finally {
      setIsGenerating(false);
      abortController.current = null;
    }
  }, [generateStory, generateActivities, onSectionUpdate]);

  // Generate both sections sequentially
  const generateBothSections = useCallback(async (lessonPlan: LessonPlan) => {
    // Clear all content first
    onSectionUpdate('introduction', { 
      story: null,
      activities: '',
      isGenerated: false,
      isStreaming: true 
    });
    onSectionUpdate('independent', { 
      story: null,
      activities: '',
      isGenerated: false,
      isStreaming: true 
    });

    try {
      await generateIntroductionSection(lessonPlan);
      
      // Check if generation was cancelled before proceeding to next section
      if (abortController.current?.signal.aborted) {
        console.log('Generation cancelled before independent section');
        return;
      }
      
      await generateIndependentSection(lessonPlan);
    } catch (error) {
      // Don't log abort errors as errors - they're intentional user actions
      if (error instanceof Error && (error.name === 'AbortError' || error.message.includes('cancel'))) {
        console.log('Generation cancelled by user');
        return;
      }
      console.error('Error generating both sections:', error);
    }
  }, [generateIntroductionSection, generateIndependentSection, onSectionUpdate]);

  return {
    generateIntroductionSection,
    generateIndependentSection,
    generateBothSections,
    regenerateContent,
    cancelGeneration,
    streamingStates,
    streamingContent,
    isGenerating
  };
}; 